import { useMutation } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import {
  GetContractDetails,
  GetContractDetailsVariables,
} from '@/graphql/contracts/_generated_/getContractDetails.query';
import {
  UpdatePauseBilling,
  UpdatePauseBillingVariables
} from '@/graphql/contracts/_generated_/updatePauseBilling.mutation';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import UpdatePauseBillingMutation from '@/graphql/contracts/updatePauseBilling.mutation.gql';
import { ALL_ITEMS } from '@/config/common.config';
import { notificationService } from '@/services/notification/notification.client';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';

export type UpdateBillingParams = Omit<UpdatePauseBillingVariables, 'contractId'>;

export default function useUpdatePauseBilling(contractId: UUID) {
  const [_updatePauseBilling, { loading, error }] = useMutation<UpdatePauseBilling, UpdatePauseBillingVariables>(
    UpdatePauseBillingMutation,
    {
      client: apolloClientContracts,
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      update: (_, { data }) => {
        if (!data) return;

        const cachedContract = apolloClientContracts.readQuery<GetContractDetails, GetContractDetailsVariables>({
          query: GetContractDetailsQuery,
          variables: { contractId }
        });

        const updatedContract: Contract = {
          ...cachedContract!.contract,
          billing: data.updatePauseBilling.billing
        };

        apolloClientContracts.writeQuery<GetContractDetails, GetContractDetailsVariables>({
          query: GetContractDetailsQuery,
          variables: { contractId },
          data: {
            contract: updatedContract
          }
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: { eq: contractId }
            },
            first: ALL_ITEMS
          }
        }
      ],
      onCompleted: () => {
        notificationService.open({
          text: 'Pause billing details successfully changed',
          title: 'Pause billing',
          type: 'success'
        });
      }
    }
  );

  const updatePauseBilling = (inputParams: UpdateBillingParams) => {
    _updatePauseBilling({
      variables: {
        contractId,
        ...inputParams
      }
    });
  };

  return {
    updatePauseBilling,
    isLoadingUpdatePauseBilling: loading,
    errorUpdatePauseBilling: error,
  } as const;
}
